import { useState, useEffect, ReactElement } from 'react';
import { Button, Card, CardContent } from '@mui/material';
import { ButtonWithDialog, Dropdown, LoadingFragment, LambdaWebFunctionsTable, PageTitle } from '@localstack/ui';
import { useRoutes, useAwsGetter, useAwsKeysMemo, useAwsEffect } from '@localstack/services';

import { LAMBDA_DEFAULT_PAGE_SIZE } from '@localstack/constants';
import { ListWebFunctionsResponse } from '@localstack/types';

import { LambdaProps } from './types';
import { NavTabs } from './components/NavTabs';
import { LambdaWebVersionGate } from './components/LambdaWebVersionGate';

export const LambdaWebFunctions = (props: LambdaProps): ReactElement => (
  <LambdaWebVersionGate {...props}>
    <LambdaWebFunctionsContent {...props} />
  </LambdaWebVersionGate>
);

const LambdaWebFunctionsContent = ({ Layout, clientOverrides, routes }: LambdaProps): ReactElement => {
  const { goto } = useRoutes();
  const [selected, setSelected] = useState<string[]>([]);
  const { deleteWebFunction, isLoading: deleting } = useAwsEffect('LambdaWeb', ['deleteWebFunction'], {
    clientOverrides,
    // Creation refreshes detail caches if a name is reused. Do not fetch deleted resources here.
    revalidate: ['listWebFunctions'],
  });
  const handleDelete = async () => {
    await Promise.all(selected.map((functionName) => deleteWebFunction({ functionName })));
    setSelected([]);
  };
  const [page, setPage] = useState<number>(0);
  const [paginationKey, setPaginationKey] = useState<string | undefined>(undefined);

  const {
    data: functions,
    isLoading,
    mutate,
  } = useAwsGetter(
    'LambdaWeb',
    'listWebFunctions',
    [{ maxResults: LAMBDA_DEFAULT_PAGE_SIZE, nextToken: paginationKey }],
    {
      clientOverrides,
      swrOverrides: {
        refreshInterval: (data?: ListWebFunctionsResponse) =>
          data?.functions?.some(({ state }) => state === 'Deleting') ? 5000 : 0,
      },
    },
  );

  const paginationKeys = useAwsKeysMemo(page, functions?.nextToken);

  useEffect(() => {
    setPaginationKey(paginationKeys[page]);
  }, [page]);

  return (
    <Layout
      documentTitle="Lambda: Web Functions"
      title={
        <PageTitle
          title="Web Functions"
          onMutate={mutate}
          breadcrumbs={[
            ['Lambda', () => goto(routes.RESOURCES_LAMBDA)],
            ['Web Functions', null],
          ]}
        />
      }
      tabs={<NavTabs routes={routes} />}
      actions={
        <>
          <Button variant="outlined" onClick={() => goto(routes.RESOURCES_LAMBDA_WEB_FUNCTION_NEW)}>
            Create
          </Button>
          <Dropdown label="Actions">
            <ButtonWithDialog
              componentType="MenuItem"
              disabled={!selected.length || deleting}
              dialogTitle={`Delete ${selected.length} Web Function(s)?`}
              dialogContent="Selected Web Functions and their endpoints will be permanently deleted"
              onClick={handleDelete}
            >
              Remove Selected
            </ButtonWithDialog>
          </Dropdown>
        </>
      }
    >
      <Card>
        <CardContent>
          <LoadingFragment
            loading={isLoading}
            arrayData={functions?.functions ?? []}
            variant="list"
            height={50}
            size={10}
          >
            <LambdaWebFunctionsTable
              selectable
              onSelect={setSelected}
              loading={isLoading}
              functions={functions?.functions ?? []}
              onViewFunction={(name) => goto(routes.RESOURCES_LAMBDA_WEB_FUNCTION, { name })}
              page={page}
              onPageChange={setPage}
              pageSize={LAMBDA_DEFAULT_PAGE_SIZE}
              hasMore={!!functions?.nextToken}
            />
          </LoadingFragment>
        </CardContent>
      </Card>
    </Layout>
  );
};
